import { captureEvent } from '@/shared/analytics/posthog';
import {
  loginWithGoogle,
  loginWithMicrosoft,
  signOutCurrentSession,
} from '@/shared/lib/authSession';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import { clearLoginAttempt } from './loginCompletion';

/**
 * TanStack Query mutation hooks for the imperative (non-form) auth actions: the
 * social sign-in popups (each its own mutation so a button can show its own
 * `isPending` spinner) and sign-out. Form submits (email sign-in, reset request,
 * reset confirmation) use the shared TanStack Form foundation. Successful sign-ins enter the
 * public-shell login coordinator before ordinary authenticated routes.
 */

export function useGoogleSignIn() {
  return useMutation({
    mutationFn: loginWithGoogle,
    onError: (error) => {
      if (error instanceof FirebaseError && error.code === 'auth/admin-restricted-operation') {
        captureEvent('firebase_signup_blocked', { provider: 'GOOGLE' });
      }
    },
  });
}

export function useMicrosoftSignIn() {
  return useMutation({
    mutationFn: loginWithMicrosoft,
    onError: (error) => {
      if (error instanceof FirebaseError && error.code === 'auth/admin-restricted-operation') {
        captureEvent('firebase_signup_blocked', { provider: 'MICROSOFT' });
      }
    },
  });
}

export function useSignOut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: signOutCurrentSession,
    // Every Vision Lab query is principal-owned server state. Remove all of it so
    // a subsequent sign-in cannot reuse the previous principal's data. Cancel
    // first so an in-flight response cannot repopulate the cleared cache.
    onSuccess: async () => {
      clearLoginAttempt();
      await queryClient.cancelQueries();
      queryClient.removeQueries();
    },
  });
}
